import React, { useState, useEffect, useCallback } from "react";
import { api } from "./api";
import "bootstrap/dist/css/bootstrap.min.css";
import {
  Button,
  Modal,
  Row,
  Col,
  Form,
  Table,
  Pagination, Alert, Spinner,
  Card, Badge, Tabs, Tab,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { CalendarDays, CheckCircle2, Download, IndianRupee, PackageCheck, RefreshCw, TriangleAlert } from "lucide-react";
import "react-toastify/dist/ReactToastify.css";
import "./CrateLedger.css";
import "./CrateTabs.css";
import "./CrateRange.css";
import DateRangeSelector from "../components/DateRangeSelector";
import CrateMonthGrid from "../components/CrateMonthGrid";
import "../components/CrateMonthGrid.css";

const today = new Date().toISOString().slice(0, 10);
const monthStart = `${today.slice(0, 8)}01`;
const currency = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2 });

const BillingData = () => {
  const [billingData, setBillingData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  const [totalPages, setTotalPages] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    sub_date: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD
    sub_crate: "",
    frieght_sub: "",
    deposited_crate: "",
    remark: "",
    is_verified: "Pending",
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBill] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [reportFrom, setReportFrom] = useState(monthStart);
  const [reportTo, setReportTo] = useState(today);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState("");
  const [activeTab, setActiveTab] = useState("ledger");

  const formatDate = (isoDate) => {
    if (!isoDate || isNaN(new Date(isoDate).getTime())) {
      return "N/A"; // Handle null, empty, or invalid dates
    }
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const fetchBillingData = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const { data } = await api.get(
        `/api/crates/getbyidall?page=${currentPage}&limit=${itemsPerPage}`
      );
      setBillingData(data.data);
      setTotalPages(data.totalPages);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Unable to load crate records.");
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  const fetchReport = useCallback(async () => {
    if (!reportFrom || !reportTo || reportFrom > reportTo) { setReportError("Select a valid report date range."); return; }
    setReportLoading(true); setReportError("");
    try { const { data } = await api.get("/api/crates/report", { params: { date_from: reportFrom, date_to: reportTo } }); setReport(data); }
    catch (error) { setReportError(error.response?.data?.message || "Unable to load crate completeness report."); }
    finally { setReportLoading(false); }
  }, [reportFrom, reportTo]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportReport = () => {
    if (!report) return;
    const rows = [["Date","Entry status","Entries","Submitted crates","Deposited crates","Freight amount","Pending verification"], ...report.days.map(day=>[day.date,day.hasEntry?"Submitted":"Missing",day.entryCount,day.submittedCrates,day.depositedCrates,day.freightAmount,day.pendingEntries])];
    const csv=rows.map(row=>row.map(value=>`"${String(value??"").replaceAll('"','""')}"`).join(",")).join("\n");
    const link=document.createElement("a");link.href=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));link.download=`crate-ledger-${reportFrom}-to-${reportTo}.csv`;link.click();URL.revokeObjectURL(link.href);
  };

  const handleEditChange = (e) => {
    setEditFormData({ ...editFormData, [e.target.name]: e.target.value });
  };


  const handleSaveChanges = async () => {
    if (!editFormData.sub_date || [editFormData.sub_crate, editFormData.frieght_sub, editFormData.deposited_crate].some(value => Number(value) < 0 || value === "")) return toast.warning("Enter valid non-negative crate values.");
    if ((editFormData.remark || "").trim().length > 500) return toast.warning("Remark cannot exceed 500 characters.");
    if (!window.confirm("Save changes to this crate record?")) return;
    setSaving(true);
    try {
      await api.put(`/api/crates/update/${editFormData.id}`, editFormData);
      toast.success("Crate record updated successfully.");
      await fetchBillingData();
      setShowEditModal(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update crate record.");
    } finally {
      setSaving(false);
    }
  };

  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent the default form submission behavior
    if (!formData.sub_date || [formData.sub_crate, formData.frieght_sub, formData.deposited_crate].some(value => Number(value) < 0 || value === "")) return toast.warning("Enter valid non-negative crate values.");
    if (formData.remark.trim().length > 500) return toast.warning("Remark cannot exceed 500 characters.");
    if (!window.confirm("Add this crate record?")) return;

    setSaving(true);
    try {
      // Post form data to the API
      await api.post(
        "/api/crates/create",
        formData
      );

      // Notify the user of success
      toast.success("Crate record added successfully.");

      // Fetch updated billing data to refresh the list or UI
      await fetchBillingData();

      // Reset the form fields after submission
      setFormData({
        sub_date: new Date().toISOString().slice(0, 10),
        sub_crate: "",
        frieght_sub: "",
        deposited_crate: "",
        remark: "",
        is_verified: "Pending",
      });


      // Close the modal popup
      handleClose();
    } catch (error) {
      console.error("Error adding crates data:", error);

      // Handle errors based on API response or network issues
      if (error.response) {
        // Display the error message returned by the API
        toast.error(error.response.data.message || "Unable to add crate record.");
      } else {
        // Handle generic network errors
        toast.error("Network error. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between mb-2">
        <h3>Crate Ledger</h3>
        {activeTab === "ledger" && <Button variant="success" onClick={handleShow}>
          Add New
        </Button>}
      </div>
      <Tabs activeKey={activeTab} onSelect={key=>setActiveTab(key)} className="crate-tabs mb-3">
      <Tab eventKey="crosscheck" title="Cross Check">
      <section className="crate-report">
        <div className="crate-report-head"><div><p className="crate-report-kicker">Completeness check</p><h2>Crate submission & freight report</h2><p>Check every expected calendar date and identify missing ledger entries.</p></div></div>
        <div className="crate-report-toolbar"><DateRangeSelector startDate={reportFrom} endDate={reportTo} label="Cross Check period" maxDate={new Date()} onApply={range=>{setReportFrom(range.startDate);setReportTo(range.endDate);}}/><Button variant="outline-primary" onClick={fetchReport}><RefreshCw size={16}/> Run report</Button><Button variant="outline-secondary" disabled={!report} onClick={exportReport}><Download size={16}/> Export</Button></div>
        {reportError&&<Alert variant="danger">{reportError}</Alert>}
        {reportLoading?<div className="crate-report-loading"><Spinner/><span>Checking ledger dates...</span></div>:report&&<><Row className="g-3 crate-report-stats"><Col sm={6} xl={3}><Card><CalendarDays/><span>Days submitted<strong>{report.summary.daysWithEntries} / {report.summary.expectedDays}</strong></span></Card></Col><Col sm={6} xl={3}><Card className={report.summary.missingDays?"missing":""}><TriangleAlert/><span>Missing days<strong>{report.summary.missingDays}</strong></span></Card></Col><Col sm={6} xl={3}><Card><PackageCheck/><span>Submitted crates<strong>{report.summary.submittedCrates}</strong></span></Card></Col><Col sm={6} xl={3}><Card><IndianRupee/><span>Total freight<strong>{currency.format(report.summary.freightAmount)}</strong></span></Card></Col></Row><div className="crate-report-summary"><span>Deposited crates <strong>{report.summary.depositedCrates}</strong></span><span>Total entries <strong>{report.summary.totalEntries}</strong></span><span>Pending verification <strong>{report.summary.pendingVerification}</strong></span></div>{report.missingDays.length?<Alert variant="warning" className="crate-missing-alert"><TriangleAlert size={18}/><div><strong>Missing ledger entries</strong><p>{report.missingDays.map(date=><Badge bg="danger" key={date}>{formatDate(date)}</Badge>)}</p></div></Alert>:<Alert variant="success" className="crate-complete-alert"><CheckCircle2 size={18}/> All dates in the selected period have ledger entries.</Alert>}<div className="table-responsive crate-report-days"><Table hover><thead><tr><th>Date</th><th>Status</th><th className="text-end">Entries</th><th className="text-end">Submitted crates</th><th className="text-end">Deposited crates</th><th className="text-end">Freight</th><th>Verification</th></tr></thead><tbody>{report.days.map(day=><tr key={day.date} className={!day.hasEntry?"missing-day":""}><td>{formatDate(day.date)}</td><td>{day.hasEntry?<Badge bg="success">Submitted</Badge>:<Badge bg="danger">Missing</Badge>}</td><td className="text-end">{day.entryCount||"—"}</td><td className="text-end">{day.hasEntry?day.submittedCrates:"—"}</td><td className="text-end">{day.hasEntry?day.depositedCrates:"—"}</td><td className="text-end">{day.hasEntry?currency.format(day.freightAmount):"—"}</td><td>{day.hasEntry?(day.pendingEntries?<Badge bg="warning">{day.pendingEntries} pending</Badge>:<Badge bg="success">Verified</Badge>):"—"}</td></tr>)}</tbody></Table></div></>}
      </section>
      {report&&!reportLoading&&<CrateMonthGrid days={report.days}/>} 
      </Tab>
      <Tab eventKey="ledger" title="Crate Ledger">
      {errorMessage && <Alert variant="danger">{errorMessage} <Button size="sm" variant="outline-danger" onClick={fetchBillingData}>Retry</Button></Alert>}
      {loading ? <div className="text-center p-5"><Spinner /><p className="text-muted mt-2">Loading crate records...</p></div> :
      <Table striped hover responsive className="table-bordered">
        <thead className="table-dark text-white">
          <tr>
            <th>SL</th>
            <th>sub date</th>
            <th>sub crate</th>
            <th>frieght sub</th>
            <th>deposited crate</th>
            <th>verified</th>
            {/*  <th>View</th>  */}
            <th>Edit</th>
          </tr>
        </thead>
        <tbody>
          {billingData.map((bill, index) => (
            <tr key={bill.id} style={{ lineHeight: "1", padding: "2px 0" }}>
              <td style={{ padding: "4px" }}>
                {(currentPage - 1) * itemsPerPage + index + 1}
              </td>
              <td style={{ padding: "4px" }}>{formatDate(bill.sub_date)}</td>
              <td style={{ padding: "4px" }}>{bill.sub_crate}</td>
              <td style={{ padding: "4px" }}>{bill.frieght_sub}</td>
              <td style={{ padding: "4px" }}>{bill.deposited_crate}</td>
              <td style={{ padding: "4px" }}>{bill.is_verified}</td>
              {/*  <td style={{ padding: "4px" }}>
                 <Button variant="success" size="sm" onClick={() => fetchBillingDetails(bill.id)}>
                  View
                </Button>  
              </td> */}
              <td>
                <Button variant="warning" size="sm" onClick={() => { setEditFormData(bill); setShowEditModal(true); }}>
                  Edit
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>}

      {/* Pagination */}
      <Pagination className="justify-content-end">
        <Pagination.Prev
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
        />

        {/* First page */}
        <Pagination.Item
          active={currentPage === 1}
          onClick={() => setCurrentPage(1)}
        >
          1
        </Pagination.Item>

        {/* Second page (if available) */}
        {totalPages > 1 && (
          <Pagination.Item
            active={currentPage === 2}
            onClick={() => setCurrentPage(2)}
          >
            2
          </Pagination.Item>
        )}

        {/* Next Button */}
        <Pagination.Next
          onClick={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          disabled={currentPage === totalPages}
        />
      </Pagination>
      </Tab>
      </Tabs>

      {/* Modal for adding crates data */}
      <Modal show={showModal} onHide={handleClose} backdrop="static" centered>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>Add New Entry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            {/* Invoice Details */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>sub_date</Form.Label>
                  <Form.Control
                    type="date"
                    name="sub_date"
                    value={formData.sub_date}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>sub crate</Form.Label>
                  <Form.Control
                    type="number"
                    name="sub_crate"
                    value={formData.sub_crate}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            {/* Amount Details */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>frieght sub</Form.Label>
                  <Form.Control
                    type="number"
                    name="frieght_sub"
                    value={formData.frieght_sub}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Deposited crates</Form.Label>
                  <Form.Control type="number" min="0" name="deposited_crate" value={formData.deposited_crate} onChange={handleChange} required />
                </Form.Group>
              </Col>

            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="remark"
                value={formData.remark}
                onChange={handleChange}
              />
            </Form.Group>

            {/* Submit Button */}
            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-3" disabled={saving} onClick={handleClose}>
                Close
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* View Modal at moment done done commented the code*/}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} backdrop="static" centered size="lg">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>Crate Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBill && (
            <div className="p-3">
              <div className="card shadow-sm border-0 p-3">


                {/* Billing Details Table */}
                <table className="table table-striped border">
                  <tbody>
                    <tr>
                      <th>Bill No</th>
                      <td>{selectedBill.bill_no}</td>
                    </tr>
                    <tr>
                      <th>Bill Date</th>
                      <td>{formatDate(selectedBill.bill_date)}</td>
                    </tr>
                    <tr>
                      <th>Supplier</th>
                      <td>{selectedBill.supplier}</td>
                    </tr>
                    <tr>
                      <th>Base Amount</th>
                      <td>₹{selectedBill.taxable_amt}</td>
                    </tr>
                    <tr>
                      <th>Bill Amount</th>
                      <td>₹{selectedBill.bill_amt}</td>
                    </tr>
                    <tr>
                      <th>Final Amount</th>
                      <td>₹{selectedBill.final_amt}</td>
                    </tr>
                    <tr>
                      <th>Payment Status</th>
                      <td>
                        <span className={`badge ${selectedBill.payment_status === "Paid" ? "bg-success" : "bg-warning"}`}>
                          {selectedBill.payment_status === "Paid" ? "Paid" : "Pending"}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th>Payment Mode</th>
                      <td>{selectedBill.payment_mode || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Payment Date</th>
                      <td>{formatDate(selectedBill.payment_date)}</td>
                    </tr>
                    <tr>
                      <th>Payment Reference</th>
                      <td>{selectedBill.Payment_ref || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>POS Receiving ID</th>
                      <td>{selectedBill.pos_rec_ref_id || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Remark</th>
                      <td>{selectedBill.remark || "No remarks"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} backdrop="static" onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>Edit Crate Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editFormData && (
            <Form>
              {/* Bill Number & Date */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Submited Date</Form.Label>
                    <Form.Control type="date" name="sub_date" value={editFormData.sub_date ? editFormData.sub_date.split('T')[0] : ""} className="form-control-plaintext border rounded p-2 bg-light" readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Submited Crate</Form.Label>
                    <Form.Control type="number" name="sub_crate" value={editFormData.sub_crate} className="form-control-plaintext border rounded p-2 bg-light" readOnly />
                  </Form.Group>
                </Col>
              </Row>

              {/* Amount Fields */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Frieght Subsidy</Form.Label>
                    <Form.Control type="number" name="frieght_sub" value={editFormData.frieght_sub} onChange={handleEditChange} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Deposited Crate</Form.Label>
                    <Form.Control type="number" name="deposited_crate" value={editFormData.deposited_crate} onChange={handleEditChange} />
                  </Form.Group>
                </Col>
              </Row>
              {/* Payment Fields */}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Verified Status</Form.Label>
                    <Form.Select name="is_verified" value={editFormData.is_verified} onChange={handleEditChange}>
                      <option value="Verified">Verified</option>
                      <option value="Pending">Pending</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>


              {/* Remarks */}
              <Form.Group className="mb-3">
                <Form.Label>Remarks</Form.Label>
                <Form.Control as="textarea" rows={3} name="remark" value={editFormData.remark} onChange={handleEditChange} />
              </Form.Group>

              {/* Save Button */}
              <div className="text-end">
                <Button variant="primary" disabled={saving} onClick={handleSaveChanges}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>


    </div>
  );
};

export default BillingData;
